// S9 — STATION HOME (spec 04 §4 floor row 9, slice 04-B-3).
//
// Post-scan: station name, shift banner (current shift + time left), the op
// queue cards — in THE SERVED ORDER, never re-sorted (D-072: the server's
// comparator is the only ordering authority; a client sort here would be a
// second comparator waiting to disagree). Offline: the e15 snapshot is cached
// verbatim (floor-read-cache), so this screen renders the last snapshot WITH
// its taken-at age — never a spinner forever — and "offline, never loaded" is
// its own state, not an empty queue.
//
// The screen also owns the two IDENTITY pins the op-run screen taps with
// (spec §5 r15 amendment, 2026-08-05): the station pin (the /q/wc scan or the
// picker) and the registered-device pin (`snapshot.devices` — exactly one
// active device auto-picks; more demand a choice, because guessing would
// stamp another tablet's identity on every event).

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  FloorEmpty,
  FloorError,
  FloorNeedsConnection,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
} from "../components/floor-chrome";
import { DeviceIdentity } from "../components/device-identity";
import { ScanInput } from "../components/scan-input";
import { useFloorOutbox } from "../hooks/use-floor-outbox";
import { useStationSnapshot } from "../hooks/use-station";
import {
  effectiveOpStatus,
  localizedName,
  needsReviewItems,
  parseProductionScan,
  pendingEventsForOp,
  pinDevice,
  pinnedDevice,
  pinnedStation,
  pinStation,
  resolveDevicePin,
  shiftMinutesLeft,
  type DevicePin,
  type OpStatus,
  type StationQueueOperation,
  type StationSnapshotDevice,
} from "../lib/production-floor";
import {
  useAllWorkCenters,
  useFacilities,
  useWorkCenters,
} from "../../office/production/hooks/production-queries";
import { useTerm } from "../../onboarding/hooks/terminology";
import { formatQty } from "../../office/production/lib/format";
import { formatBusinessDate } from "../../../lib/datetime";
import { RequestError } from "../../../lib/request";
import "./floor-production.css";

// ---------------------------------------------------------------------------
// The picker (/floor/station) — station identity when there is no QR in hand
// ---------------------------------------------------------------------------

export function StationPickerScreen() {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const navigate = useNavigate();
  const [facilityId, setFacilityId] = useState("");
  const [scanMiss, setScanMiss] = useState(false);
  const facilities = useFacilities();
  const facilityItems = facilities.data?.items ?? [];
  const effectiveFacility = facilityId || (facilityItems[0]?.id ?? "");
  // Active only: an archived station is one nobody may be sent to, and the
  // walkthroughs leave thousands of them at a plant (lib/work-centers.ts).
  const workCenters = useWorkCenters(
    effectiveFacility.length > 0 ? effectiveFacility : undefined,
    "active",
  );
  const pinned = pinnedStation();

  const openStation = (id: string, name?: string): void => {
    pinStation({ workCenterId: id, ...(name !== undefined ? { name } : {}) });
    void navigate({ to: "/floor/station/$workCenterId", params: { workCenterId: id } });
  };

  return (
    <FloorScreen
      wide
      title={t("production.floor.picker.title")}
      back={
        <Link to="/floor" className="floor-back">
          {t("inventory.floor.back")}
        </Link>
      }
    >
      {/* SEAM: the /q/wc grammar is scan.ts's once its wc recogniser lands
          (same slice, other sub-agent); parseProductionScan is the local
          stand-in for the picker's keyboard-wedge input. */}
      <ScanInput
        captureWedge
        onScan={(payload) => {
          const scan = parseProductionScan(payload);
          if (scan === undefined) {
            setScanMiss(true);
            return;
          }
          setScanMiss(false);
          if (scan.kind === "wc") openStation(scan.id);
          else void navigate({ to: "/q/op/$opId", params: { opId: scan.id } });
        }}
        label={t("production.floor.picker.scanLabel")}
        placeholder={t("production.floor.picker.scanPlaceholder")}
      />
      {scanMiss ? (
        <p className="floor-hint floor-hint-warn" data-testid="station-scan-miss">
          {t("production.floor.picker.scanMiss")}
        </p>
      ) : null}

      {pinned !== undefined ? (
        <p className="floor-hint" data-testid="station-pinned-note">
          <Link
            to="/floor/station/$workCenterId"
            params={{ workCenterId: pinned.workCenterId }}
            className="floor-go floor-go-lead"
          >
            {t("production.floor.picker.resume", { name: pinned.name ?? "" })}
          </Link>
        </p>
      ) : null}

      {facilityItems.length > 1 ? (
        <label className="op-field-label">
          {term("facility")}
          <select
            className="floor-select"
            value={effectiveFacility}
            onChange={(event) => {
              setFacilityId(event.target.value);
            }}
          >
            {facilityItems.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {facilities.isPending || workCenters.isPending ? <FloorSkeleton rows={3} /> : null}

      {facilities.error !== null || workCenters.error !== null ? (
        <FloorError
          title={t("production.floor.picker.errorTitle")}
          detail={t("production.floor.picker.errorBody")}
          requestId={
            facilities.error instanceof RequestError
              ? facilities.error.requestId
              : workCenters.error instanceof RequestError
                ? workCenters.error.requestId
                : undefined
          }
          onRetry={() => {
            void facilities.refetch();
            void workCenters.refetch();
          }}
        />
      ) : null}

      {workCenters.data !== undefined ? (
        workCenters.data.items.length === 0 ? (
          <FloorEmpty
            title={t("production.floor.picker.emptyTitle")}
            body={t("production.floor.picker.emptyBody")}
          />
        ) : (
          <ul className="floor-cards" data-testid="station-picker-list">
            {workCenters.data.items.map((wc) => {
              const name = localizedName(wc.name_i18n, i18n.language, wc.code);
              return (
                <li key={wc.id}>
                  {/* The whole card is the target (D-444 ruling 6). A button,
                      not a link, because choosing a station also pins it to
                      this tablet before it opens. */}
                  <button
                    type="button"
                    className="floor-card floor-card-link"
                    data-testid="station-pick"
                    onClick={() => {
                      openStation(wc.id, name);
                    }}
                  >
                    <span className="floor-card-title">{name}</span>
                    <span className="floor-card-meta">{wc.code}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </FloorScreen>
  );
}

// ---------------------------------------------------------------------------
// S9 (/floor/station/$workCenterId)
// ---------------------------------------------------------------------------

export function StationHomeScreen() {
  const { t, i18n } = useTranslation();
  const { workCenterId } = useParams({ from: "/floor/station/$workCenterId" });
  const read = useStationSnapshot(workCenterId);
  const outbox = useFloorOutbox();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [devicePick, setDevicePick] = useState<DevicePin | undefined>(() => pinnedDevice());
  // The station's NAME, which the QR path never carries (D-119). Seeded from
  // the pin so a tablet that has one shows it before any read resolves, and
  // filled in from the work-centre reference read when it does not.
  const [stationName, setStationName] = useState<string | undefined>(() => {
    const pin = pinnedStation();
    return pin?.workCenterId === workCenterId ? pin.name : undefined;
  });

  // The shift banner's "time left" is a clock fact — refreshed on a coarse
  // tick so the banner cannot claim a shift that ended an hour ago.
  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, 30_000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  // Keep the station pin honest: landing here (deep link, /q/wc redirect) IS
  // the pinning act — the station QR is the context anchor (§3.3 step 2).
  // `pinStation` inherits a known name for the SAME work centre (D-119), so a
  // re-assertion of where the tablet is never erases which machine it is.
  useEffect(() => {
    const pin = pinnedStation();
    if (pin?.workCenterId !== workCenterId) {
      pinStation({ workCenterId });
      setStationName(undefined);
    }
  }, [workCenterId]);

  // NAMING THE MACHINE (D-119). The `/q/wc/` scan is the production path and it
  // carries an id only; `StationSnapshot` carries devices, a shift and a queue
  // and no station identity at all. So the name is resolved from the one
  // reference read that has it — the same endpoint the picker uses, therefore
  // the same permission — and persisted into the pin, which is what makes the
  // NEXT cold offline start able to name the machine. Only fetched while the
  // name is unknown: a tablet bolted to one machine asks once, ever.
  const workCentres = useAllWorkCenters(stationName === undefined);
  useEffect(() => {
    if (stationName !== undefined) return;
    const row = workCentres.data?.items.find((wc) => wc.id === workCenterId);
    if (row === undefined) return;
    const name = localizedName(row.name_i18n, i18n.language, row.code);
    pinStation({ workCenterId, name });
    setStationName(name);
  }, [stationName, workCentres.data, workCenterId, i18n.language]);

  const snapshot = read.data;
  const devices: readonly StationSnapshotDevice[] = snapshot?.devices ?? [];

  // Device identity: persisted pick wins; exactly one active device
  // auto-picks and PERSISTS (so the op-run screen sees it offline too).
  useEffect(() => {
    if (snapshot === undefined) return;
    const resolved = resolveDevicePin(devices, devicePick);
    if (resolved !== undefined && resolved.id !== devicePick?.id) {
      pinDevice(resolved);
      setDevicePick(resolved);
    }
    // devices derives from snapshot; devicePick covers the manual path.
  }, [snapshot, devices, devicePick]);

  const review = needsReviewItems(outbox.items);

  const queueOps: readonly StationQueueOperation[] = snapshot?.queue.operations ?? [];

  // §3.4: each card shows THIS DEVICE's view — the snapshot status with the
  // device's own queued taps folded over it (needs_review never folds). A
  // worker who started an op offline finds it under "my open steps", not
  // frozen at the snapshot's `ready`.
  const effectiveStatuses = useMemo(() => {
    const map = new Map<string, OpStatus>();
    for (const op of queueOps) {
      map.set(op.id, effectiveOpStatus(op.status, pendingEventsForOp(outbox.items, op.id)));
    }
    return map;
  }, [queueOps, outbox.items]);
  const statusOf = (op: StationQueueOperation): OpStatus =>
    effectiveStatuses.get(op.id) ?? op.status;

  // A FILTER over the served array — the relative order is untouched (D-072).
  const myOpenOps = useMemo(
    () =>
      queueOps.filter((op) => {
        const status = effectiveStatuses.get(op.id) ?? op.status;
        return status === "in_progress" || status === "paused";
      }),
    [queueOps, effectiveStatuses],
  );

  return (
    <FloorScreen
      title={stationName ?? t("production.floor.station.title")}
      back={
        <Link to="/floor/station" className="floor-back">
          {t("production.floor.station.changeStation")}
        </Link>
      }
    >
      <SnapshotNote
        source={read.source}
        cachedAt={read.cachedAt}
        revalidating={read.revalidating}
      />

      {review.length > 0 ? (
        <p className="op-review-note" data-testid="station-review-note">
          <Link to="/floor/tray">
            {t("production.floor.station.needsReview", { count: review.length })}
          </Link>
        </p>
      ) : null}

      {read.isLoading && snapshot === undefined ? <FloorSkeleton rows={4} /> : null}

      {snapshot === undefined && !read.isLoading && read.source === "none" && !read.online ? (
        <FloorNeedsConnection what={t("production.floor.station.what")} />
      ) : null}

      {snapshot === undefined && read.error !== null && read.error !== undefined && read.online ? (
        <FloorError
          title={t("production.floor.station.errorTitle")}
          detail={t("production.floor.station.errorBody")}
          requestId={read.error instanceof RequestError ? read.error.requestId : undefined}
          onRetry={() => {
            read.refetch();
          }}
        />
      ) : null}

      {snapshot !== undefined ? (
        <>
          <div className="station-shift" data-testid="station-shift">
            {/* THE SHIFT, IN THE OPERATOR'S LANGUAGE. This printed
                `shift.code` — `DAY` — at a Russian operator, while the
                tenant's own `Дневная смена` sat in `shift_definitions.
                name_i18n`: the code is a stable IDENTIFIER the tenant keys on,
                never a label. The same resolver every other floor label uses
                (active locale → `en` → the code): a tenant that authored no
                Russian name reads the English one, and one that authored no
                name at all — or a snapshot cached before the name crossed the
                wire — reads the code, because a fake translation is worse than
                a missing one (`ShiftLocaleName`). */}
            <span className="station-shift-name" data-testid="station-shift-name">
              {localizedName(snapshot.shift.name_i18n, i18n.language, snapshot.shift.code)}
            </span>
            {/* THE APP'S ONE DATE RENDERER (D-121). This printed the wire's ISO
                `2026-08-05` straight onto the glass — the exact defect
                `src/lib/datetime.ts` exists to end, and one the floor is worse
                placed to carry than the office: an operator reading it at a
                metre in gloves gets a machine format nobody speaks, in a plant
                whose working language is Russian. */}
            <span>{formatBusinessDate(snapshot.shift.shift_date, i18n.language)}</span>
            <span className="station-shift-left">
              {t("production.floor.station.shiftLeft", {
                minutes: shiftMinutesLeft(snapshot.shift, nowMs),
              })}
            </span>
          </div>

          <DeviceIdentity
            devices={devices}
            pick={devicePick}
            onPick={(pin) => {
              pinDevice(pin);
              setDevicePick(pin);
            }}
          />

          {/* Module 05 S8/S9 (slice 05-B): waste capture + remnant intake ride
              the station context — same anchor, same outbox. */}
          <div className="station-waste-actions" data-testid="station-waste-actions">
            <Link
              to="/floor/station/$workCenterId/waste"
              params={{ workCenterId }}
              className="floor-go"
              data-testid="station-waste-link"
            >
              {t("waste.floor.captureTitle")}
            </Link>
            <Link
              to="/floor/station/$workCenterId/remnant"
              params={{ workCenterId }}
              className="floor-go"
              data-testid="station-remnant-link"
            >
              {t("waste.floor.remnantTitle")}
            </Link>
          </div>

          {myOpenOps.length > 0 ? (
            <section className="floor-group" data-testid="station-open-ops">
              <h2 className="floor-group-head">{t("production.floor.station.myOpen")}</h2>
              <ul className="floor-cards">
                {myOpenOps.map((op) => (
                  <QueueCard
                    key={op.id}
                    op={op}
                    effectiveStatus={statusOf(op)}
                    workCenterId={workCenterId}
                  />
                ))}
              </ul>
            </section>
          ) : null}

          <section className="floor-group">
            <h2 className="floor-group-head">{t("production.floor.station.queue")}</h2>
            {queueOps.length === 0 ? (
              <FloorEmpty
                title={t("production.floor.station.emptyTitle")}
                body={t("production.floor.station.emptyBody")}
              />
            ) : (
              <ul className="floor-cards" data-testid="station-queue">
                {/* SERVED ORDER — rendered as the array came off the wire (or
                    out of the verbatim cache). Never sorted here (D-072). */}
                {queueOps.map((op) => (
                  <QueueCard
                    key={op.id}
                    op={op}
                    effectiveStatus={statusOf(op)}
                    workCenterId={workCenterId}
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </FloorScreen>
  );
}

function QueueCard({
  op,
  effectiveStatus,
  workCenterId,
}: {
  op: StationQueueOperation;
  /** The DEVICE's view (§3.4): snapshot status + this device's queued taps. */
  effectiveStatus: OpStatus;
  workCenterId: string;
}) {
  const { t, i18n } = useTranslation();
  const late =
    op.due_date !== null &&
    effectiveStatus !== "completed" &&
    Date.parse(`${op.due_date}T23:59:59`) < Date.now();
  return (
    <li className="floor-card" data-testid="station-queue-card" data-op-id={op.id}>
      <div className="station-card-row">
        <p className="floor-card-title">{op.wo_no}</p>
        <span className="station-card-status" data-status={effectiveStatus}>
          {t(`production.status.op.${effectiveStatus}`)}
        </span>
        {/* 04-C-5's TWO CARD STATES. Both ride `StationQueueOperation` (§5 r15)
            and both were on the wire, documented in the schema as station-card
            behaviour, and rendered by nothing on the floor until adversarial
            review found it — the office S2 row got the badges and the operator
            did not.

            `qc_hold` — §2.2. The schema's own words are "the station card says
            the step is held BEFORE the operator walks to it and taps Start into
            a refusal", and that is exactly the walk this badge saves. §3.2
            refuses op_started/op_resumed/op_completed/qty_reported while it is
            set, so an unmarked card sends a worker to a machine to be told no.

            `rework_reopened_at` — §3.2's rework re-open, which is LIVE in this
            release (a supervisor clicks Rework on S5 today and the step lands
            back in this queue). While it is set, qty reports on the step default
            `is_rework = true` and labor lands `entry_kind = 'rework'`: the
            operator is being paid under a different rule and a card that looked
            like new work would not say so. */}
        {op.rework_reopened_at !== null ? (
          <span className="station-card-flag" data-testid="station-card-rework">
            {t("production.floor.station.reworkBadge")}
          </span>
        ) : null}
        {op.qc_hold ? (
          <span className="station-card-flag is-hold" data-testid="station-card-hold">
            {t("production.floor.station.holdBadge")}
          </span>
        ) : null}
        {effectiveStatus !== op.status ? (
          <span className="op-pending-sync" data-testid="station-pending-sync">
            {t("production.floor.pendingSync")}
          </span>
        ) : null}
      </div>
      <p className="floor-card-sub">{op.product_name}</p>
      <div className="station-card-row">
        <span className="floor-card-measure">
          {t("production.floor.station.progress", {
            reported: formatQty(op.qty_reported, { locale: i18n.language, maxDp: 3 }),
            planned: formatQty(op.qty_planned, { locale: i18n.language, maxDp: 3 }),
          })}
        </span>
        <span>{t("production.common.priorityLabel", { value: op.priority })}</span>
        {op.due_date !== null ? (
          <span className="station-card-due" data-late={late ? "true" : "false"}>
            {formatBusinessDate(op.due_date, i18n.language)}
          </span>
        ) : null}
      </div>
      <Link
        to="/floor/station/$workCenterId/op/$opId"
        params={{ workCenterId, opId: op.id }}
        className="floor-go floor-go-lead"
        data-testid="station-queue-open"
      >
        {t("production.floor.station.openOp")}
      </Link>
    </li>
  );
}

// The local `formatQty` that used to live here is GONE (D-121). It took a
// `_locale` it ignored and stripped trailing zeros with two regexes — so a
// station card read `1234.5` while the office S5 row, the wall board and this
// worker's own shift screen rendered the identical wire value as `1 234,5` in
// ru. One physical quantity, two vocabularies, which is the defect
// `production/lib/format.ts` was extracted to end. The shared formatter is
// still exact (scaled BigInt, never float math on a `numeric(14,3)` string —
// D-041); it simply also groups and separates in the reader's language.
